<template>
  <div class="queue-view">
    <section class="panel-header-section">
      <h2 class="section-title">Task Queue</h2>
      <p class="section-subtitle">Incoming messages, scheduled jobs, and system events.</p>
    </section>

    <section class="queue-grid">
      <div class="queue-card primary">
        <div class="queue-label">Channel A</div>
        <div class="queue-value">{{ data1 }}</div>
        <div class="queue-bar">
          <div class="queue-fill" :style="{ width: Math.min(data1 * 10, 100) + '%' }"></div>
        </div>
      </div>

      <div class="queue-card secondary">
        <div class="queue-label">Channel B</div>
        <div class="queue-value">{{ data2 }}</div>
        <div class="queue-bar">
          <div class="queue-fill" :style="{ width: Math.min(data2 * 10, 100) + '%' }"></div>
        </div>
      </div>
    </section>

    <section class="log-panel">
      <div class="log-header">
        <h3>Event Log</h3>
        <button class="refresh-btn" @click="getQueue">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
          <span>Refresh</span>
        </button>
      </div>
      <div class="log-body">
        <div v-if="error" class="log-entry error">
          <span class="log-time">{{ currentTime }}</span>
          <span class="log-message">{{ error.statusCode }} — {{ error.message }}</span>
        </div>
        <div v-else class="log-entry">
          <span class="log-time">{{ currentTime }}</span>
          <span class="log-message">{{ formattedData }}</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'QueueView',
  props: {
    msg: String
  },
  data() {
    return {
      data1: 0,
      data2: 0,
      error: '',
      currentTime: ''
    }
  },
  computed: {
    formattedData() {
      if (this.error) return ''
      return `Queue payload received: ${JSON.stringify(this.error || this.data1)}`
    }
  },
  methods: {
    getQueue() {
      this.currentTime = new Date().toLocaleTimeString()
      this.axios.get(process.env.VUE_APP_BACK_URL + '/queue')
          .then(res => {
            this.error = ''
            const payload = res.data
            if (typeof payload === 'number') {
              this.data1 = payload
              this.data2 = Math.floor(payload / 2)
            } else {
              this.data1 = 0
              this.data2 = 0
            }
          })
          .catch(err => {
            if (err.response && err.response.status >= 500) {
              this.error = { statusCode: err.response.status, message: 'Server error' }
            } else if (err.response) {
              this.error = { statusCode: err.response.status, message: err.response.data.message || 'Request failed' }
            } else {
              this.error = { statusCode: 0, message: err.message }
            }
          })
    }
  },
  mounted() {
    this.getQueue()
  }
}
</script>

<style scoped>
.queue-view {
  max-width: 1200px;
}

.panel-header-section {
  margin-bottom: 28px;
}

.section-title {
  font-family: var(--font-display);
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0 0 8px;
}

.section-subtitle {
  color: var(--text-secondary);
  margin: 0;
}

.queue-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 28px;
}

.queue-card {
  padding: 24px;
  border-radius: var(--radius);
  background: var(--surface);
  border: 1px solid var(--border);
}

.queue-card.primary {
  border-left: 3px solid var(--accent);
}

.queue-card.secondary {
  border-left: 3px solid var(--warning);
}

.queue-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
  margin-bottom: 10px;
}

.queue-value {
  font-family: var(--font-mono);
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 16px;
}

.queue-bar {
  height: 6px;
  border-radius: 3px;
  background: rgba(148, 163, 184, 0.12);
  overflow: hidden;
}

.queue-fill {
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--accent), #0066ff);
  transition: width 0.4s ease;
}

.queue-card.secondary .queue-fill {
  background: linear-gradient(90deg, var(--warning), #ff5252);
}

.log-panel {
  border-radius: var(--radius);
  background: var(--surface);
  border: 1px solid var(--border);
  overflow: hidden;
}

.log-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid var(--border);
}

.log-header h3 {
  font-family: var(--font-display);
  font-size: 1.05rem;
  margin: 0;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.refresh-btn svg {
  width: 16px;
  height: 16px;
}

.log-body {
  padding: 20px 22px;
  font-family: var(--font-mono);
  font-size: 0.85rem;
}

.log-entry {
  display: flex;
  gap: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

.log-entry.error {
  background: var(--danger-bg);
  color: var(--danger);
  border: 1px solid rgba(255, 82, 82, 0.2);
}

.log-time {
  color: var(--text-muted);
  flex-shrink: 0;
}

.log-message {
  word-break: break-word;
}

@media (max-width: 560px) {
  .queue-grid {
    grid-template-columns: 1fr;
  }

  .log-entry {
    flex-direction: column;
    gap: 6px;
  }
}
</style>
